import {compact} from '@benchling/js-utils';
import React from 'react';

import {CoreObjectArchiveData} from 'app-core/platform/archivable/CoreObjectArchiveConstants';
import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import BenchlingConfig from 'BenchlingConfig';
import FolderSelector from 'file-browser/FolderSelector';
import {CustomTag} from 'folder-items/FileConstants';
import AliasField from 'metadata/AliasField';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import Avatar, {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Title} from 'ui-library/Headers';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

import DataCatalogTagTable from './DataCatalogTagTable';

interface DataCatalogItem {
  id: string;
  name: string;
  creator: HasAvatar;
  createdAt: string;
  customFields: Array<CustomTag>;
  modifiedAt: string;
  folder: {id: string};
  permissions: {writable: boolean; archivable: boolean | null; unarchivable: boolean | null};
  archiveData: CoreObjectArchiveData;
}

export interface DataCatalogItemActionIcon {
  icon: BenchlingIconName;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}
interface DataCatalogItemDetailsProps {
  dataCatalogItem: DataCatalogItem;
  extraColumns?: Array<JSX.Element>;
  tagsEmptyMessage: string;
  onChange: (updateParams: {
    id: string;
    name?: string;
    folderId?: string;
    customFields?: {[name: string]: string};
  }) => Promise<void>;
  actionIcons?: Array<DataCatalogItemActionIcon>;
  children?: React.ReactNode;
}

export default function DataCatalogItemDetails({
  dataCatalogItem,
  extraColumns,
  onChange,
  tagsEmptyMessage,
  actionIcons,
  children,
}: DataCatalogItemDetailsProps): JSX.Element {
  const columns = compact(
    [
      <>
        <Title>Creator</Title>
        <div>
          <Avatar
            className="u-mini-spacing-right"
            size={AvatarSize.XXSMALL}
            entity={dataCatalogItem.creator}
          />
          {dataCatalogItem.creator.name}
        </div>
      </>,
      <>
        <Title>Folder</Title>
        <FolderSelector
          value={dataCatalogItem.folder.id}
          onChange={(e: {target: {value: string}}) => {
            onChange({id: dataCatalogItem.id, folderId: e.target.value});
          }}
          showInventory={true} // Shows folders in addition to project
          readOnly={!dataCatalogItem.permissions.writable}
          unsetOnFetchFail={false}
        />
      </>,
      <>
        <Title>Created</Title>
        {readableTime(dataCatalogItem.createdAt)}
      </>,
      <>
        <Title>Modified</Title>
        {readableTime(dataCatalogItem.modifiedAt)}
      </>,
      BenchlingConfig.STUDIES_ENABLE ? (
        <>
          <Title>Studies</Title>
          <EditableStudiesList
            studyable={dataCatalogItem}
            isWritable={dataCatalogItem.permissions.writable}
          />
        </>
      ) : null,
    ].concat(extraColumns ?? [])
  );

  const metadata = [];
  for (let i = 0, end = columns.length; i < end; i += 2) {
    metadata.push(
      <Row bufferRowReset={true} key={i}>
        <div className="metadata-section">
          <Col sm={6}>{columns[i]}</Col>
        </div>
        <div className="metadata-section">
          <Col sm={6}>{columns[i + 1]}</Col>
        </div>
      </Row>
    );
  }

  return (
    <div className="react-tab-container">
      <div>
        <div>
          <Row bufferRowReset={true}>
            <Col xs={6}>
              <AliasField
                initialAlias={dataCatalogItem.name}
                isFullWidth={true}
                isHeader={true}
                isWritable={dataCatalogItem.permissions.writable}
                complete={(name: string) => {
                  onChange({id: dataCatalogItem.id, name});
                }}
                placeholder="Name"
              />
              <ArchiveRecordLabel item={dataCatalogItem} />
            </Col>
            <Col xs={6}>
              <div className="u-justify-content-end">
                {actionIcons?.map(({icon, title, onClick, disabled}) => (
                  <Tooltip text={title} key={`${icon}-${title}`}>
                    <Button
                      buttonStyle={ButtonStyle.SECONDARY}
                      className="metadata-header-button-spacing-left"
                      icon={icon}
                      disabled={disabled}
                      onClick={onClick}
                      size={ButtonSize.SMALL}
                    />
                  </Tooltip>
                ))}
                <ArchiveOrUnarchiveButton
                  className="metadata-header-button-spacing-left"
                  isArchivable={Boolean(dataCatalogItem.permissions.archivable)}
                  isUnarchivable={Boolean(dataCatalogItem.permissions.unarchivable)}
                  item={dataCatalogItem}
                  onArchive={ArchiveActions.bulkArchiveFilesWithDataMods}
                  onUnarchive={ArchiveActions.bulkUnarchiveFilesWithDataMods}
                />
                <ExportAuditLogButton
                  className="metadata-header-button-spacing-left"
                  ids={[dataCatalogItem.id]}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div className="u-spacing-top">
        <FormGroup>{metadata}</FormGroup>
      </div>
      <div className="u-spacing-top">
        <DataCatalogTagTable
          emptyMessage={tagsEmptyMessage}
          isWritable={dataCatalogItem.permissions.writable}
          itemId={dataCatalogItem.id}
          initialTags={dataCatalogItem.customFields}
          updateTags={async (tags: Array<CustomTag>) => {
            const customFields: {[name: string]: string} = {};
            for (const tag of tags) {
              customFields[tag.name] = tag.value!;
            }
            await onChange({id: dataCatalogItem.id, customFields});
          }}
        />
      </div>
      {children}
    </div>
  );
}
